import { BaseClientConnector } from "./ClientConnectors/BaseClientConnector.js";
import { PROTOCOLS, PEER_TO_PEER_PROTOCOLS, PEER_CONNECTION_STATUS, ReceivedMessage, SystemMessage, ensureArrayBuffer } from "@gotti/core";
import { BasePeerConnection } from "./BasePeerConnection.js";

export enum SocketType {
  UDP,
  TCP
}

export interface PeerConnectionConfig {
  iceServers?: Array<RTCIceServer>,
  socketType?: SocketType,
  timeout?: number,
  retries?: number,
  retryTimeout?: number,
}

export class PlayerPeerConnection extends BasePeerConnection {
    readonly peerPlayerPublicId: string | number;
    readonly clientPlayerPublicId: string | number;
    private connector: BaseClientConnector;
    private last5Pings: Array<number> = [];
    public status : PEER_CONNECTION_STATUS = PEER_CONNECTION_STATUS.NONE;
    private pingInterval: NodeJS.Timeout | null = null;
    private sentPingAt: number;
  private _ping: number = 0;
    private missedPings: number = 0;

    private seq: number = 0;

    public gotAck: boolean=false;

    readonly peerSystem: string | number;

    private _onMessage : (msg: SystemMessage | ReceivedMessage) => void
    private _onAck: () => void
    private _onceAcked: () => void;
    private _onConnected : (opts?: any) => void
    private _onDisconnected : (reason?: string) => void
    private _onMissedPing : (missed: number) => void
    private _onRawMessage?: (frame: ArrayBuffer) => void

    constructor(
      connector: BaseClientConnector,
      clientPlayerPublicId: string | number,
      peerPlayerPublicId: string | number, peerSystem: string | number,
      configOptions?: PeerConnectionConfig,
      status?: PEER_CONNECTION_STATUS
    ) {
        // create unique channel id for players by ordering by index then joining
      super({
        channelName: [peerPlayerPublicId, clientPlayerPublicId].sort().join('-'),
        ...configOptions,
      })  

      this.connector = connector;
      this.peerSystem = peerSystem
      this.peerPlayerPublicId = peerPlayerPublicId;
      this.clientPlayerPublicId = clientPlayerPublicId;

      if(status !== undefined && status !== null) {
        this.status = status;
      }      
    }
  
    onIceCandidate(event: { candidate?: RTCIceCandidate; }): void {
      if(event.candidate) {
        this.connector.send([PROTOCOLS.PEER_SIGNAL_REQUEST, this.peerPlayerPublicId, { candidate: event.candidate }]);
      }
    }

    onDataChannelOpen(_label: string) {
      if(!this.connected) {
        this.connected = true;
        if(!this.pingInterval) {
          this.startPinging();
        }
        this.status = PEER_CONNECTION_STATUS.CONNECTED;
        this.connectedAt = Date.now();
        this._onConnected?.(this.peerHandshakeOptions);
      }
    }

    public onMessage(cb: (msg: SystemMessage) => void) {
      this._onMessage = cb;
    }
    public onRawMessage(cb: (frame: ArrayBuffer) => void) {
      this._onRawMessage = cb;
    }
    public onceAcked(cb: () => void) {
      this._onceAcked = () => {
        cb();
        delete this._onceAcked
      }
    }
    public onAck(cb: () => void) {
      this._onAck = cb;
    }
    public onMissedPing(cb: (missedPings: number) => void) {
      this._onMissedPing = cb;
    }
    public onConnected(cb: (opts?: any) => void) {
      this._onConnected = cb;
    }
    public offConnected() {
      delete this._onConnected;
    }
    public onDisconnected(cb: (reason?: string) => void) {
        this._onDisconnected = cb;
    }
    public offDisconnected(cb: (reason?: string) => void) {
        delete this._onDisconnected;
    }

    public checkAck() {
        if(!this.gotAck) {
            this._onAck?.()
            this._onceAcked?.()
            this.gotAck = true;
        }
    }

    public clearAckListeners() {
        delete this._onAck;
        delete this._onceAcked;
    }

    onLocalDescription(event: { sdp: RTCLocalSessionDescriptionInit; isInitial?: boolean, requestOptions?: any }): void {
        if(event.isInitial) {
          const { systemName, ...restOfOptions } = event.requestOptions;
          this.connector.send([PROTOCOLS.PEER_CONNECTION_REQUEST, this.peerPlayerPublicId, { sdp: event.sdp }, systemName, restOfOptions]);
        } else {
          // console.log('SEND UP PEER SIGNAL REQUESThhandleLocalDescription')
          this.connector.send([PROTOCOLS.PEER_SIGNAL_REQUEST, this.peerPlayerPublicId, { sdp: event.sdp}])
        }
    }

  onConnectionClose(_label: string) {
      if(this.connected) {
          if(this.pingInterval) {
              clearInterval(this.pingInterval);
              this.pingInterval = null;
          }
          this.connectedAt = -1;
          this.status = PEER_CONNECTION_STATUS.NONE;
          this.connected = false;
          this._onDisconnected?.('Datachannel closed.');
      }
    }

    private startPinging() {
        let lastSent = this.seq;
        this.sentPingAt = Date.now();
        this.send(JSON.stringify([PEER_TO_PEER_PROTOCOLS.PING, this.seq]));
        this.pingInterval = setInterval(() => {
            // this.seq should increase by 1 by the time we get back here
            if(lastSent !== this.seq - 1) {
                this.missedPings++;
                this.seq = 0;
                lastSent = 0;
                this._onMissedPing?.(this.missedPings);
            } else {
                this.missedPings = 0;
                lastSent++;
            }
            this.sentPingAt = Date.now();
            this.send(JSON.stringify([PEER_TO_PEER_PROTOCOLS.PING, this.seq]));
        }, this.config.timeout);
    }

    private handlePong(seq) {
        const ping = Date.now() - this.sentPingAt;
        if(seq === this.seq) { // we good they sent back our seq
            this.seq++;
            if (this.last5Pings.length > 4) {
                // micro optimization hand implemented shift
                this.last5Pings[5] = this.last5Pings[4];
                this.last5Pings[4] = this.last5Pings[3];
                this.last5Pings[3] = this.last5Pings[2];
                this.last5Pings[2] = this.last5Pings[1];
                this.last5Pings[1] = this.last5Pings[0];
                this.last5Pings[0]  = ping;
          this._ping = (this.last5Pings[0] + this.last5Pings[1] + this.last5Pings[2] + this.last5Pings[3] + this.last5Pings[4]) / 5;
            } else {
                this.last5Pings.push(ping);
                const total = this.last5Pings.reduce((acc, c) => acc + c, 0);
          this._ping = total / this.last5Pings.length;
            }
        } else {
            throw new Error(`INVALID SEQUENCE ${seq} SENT BACK WHEN WE EXPECTED ${this.seq}`);
        }
    }

  onPeerMessage(_label: string, data: any) {
    if(typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
      const frame = ensureArrayBuffer(data as Buffer);
      this._onRawMessage?.(frame);
      return;
    }
    if(data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
      const frame = ensureArrayBuffer(data as ArrayBuffer | ArrayBufferView);
      this._onRawMessage?.(frame);
      return;
    }
    const payload = typeof data === 'string' ? data : typeof data === 'object' && data !== null && 'toString' in data ? data.toString() : String(data);
    const decoded = JSON.parse(payload);
        if(decoded.length < 3) {
            if(decoded[0] === PEER_TO_PEER_PROTOCOLS.PING) {
                this.send(JSON.stringify([PEER_TO_PEER_PROTOCOLS.PONG, decoded[1]]));
            } else if (decoded[0] === PEER_TO_PEER_PROTOCOLS.PONG) {
                this.handlePong(decoded[1]);
            }
        } else {
           this._onMessage?.({
                type: decoded[0],
                data: decoded[1],
                to: decoded[2],
           })
        }
    }

    public get ping(): number {
      return this._ping;
    }
}